import React, { useContext, useEffect, useRef, useState } from 'react';

import {
	Box,
	Button,
	IconButton,
	Typography,
	useTheme,
	Stack,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import EdgeLabel from '../../../../../../../../enums/edgeLabel';
import BlockType from '../../../../../../../../enums/blockType';
import { getOppositeEdge } from '../../../../utils/edgeUtils';
import CloseIcon from '@mui/icons-material/Close';
import Segment from './Segment';
import BlockOptions from './BlockOptions';
import { DeckContext } from '../../../../context/DeckContextV3';
import SegmentOptions from './SegmentOptions';
import getBlock from 'src/utils/getBlockV3';
import ArrowPopper from '../../ArrowPopper';
import getParentBlock from '../../../../../../../../utils/getParentBlock';
import FeetInchesInput from '../../../FeetInchesInputV2';
import { toastError } from 'src/Toaster/Toast';
import { MAX_STAIR_HEIGHT } from 'src/constants/limits';
import { Block } from 'src/types/shared/blocksV3';
import updateChildBlocks from '../../../../utils/updateChildBlocks';
import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from 'src/constants/coefficients';
import { v4 as uuidv4 } from 'uuid';
import EdgeType from 'src/enums/edgeType';

const EdgeModalV3: React.FC = () => {
	const { state, dispatch, transformWrapperRef } = useContext(DeckContext);
	const theme = useTheme();
	const clickAwayLockedRef = useRef(false);

	const [segmentLength, setSegmentLength] = useState(0);
	const [segmentType, setSegmentType] = useState<string>('');

	const selectedEdge = state.selectedEdge;
	const block = getBlock(state.blocks, selectedEdge?.blockId);

	const blockEdge = block?.edges.find(
		(edge) => edge.label == selectedEdge.edgeLabel
	);

	const [stage, setStage] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const displayWidthDimension =
		blockEdge?.label == 'Top' || blockEdge?.label == 'Bottom'
			? 'width'
			: 'depth';

	useEffect(() => {
		if (stage !== 2) {
			setSegmentLength(0);
			setSegmentType('');
		}
	}, [stage]);

	const anchorEl = block?.ref?.current;

	const animationInProgress = useRef(false);
	const currentBlockId = useRef<string | null>(null);
	const currentEdgeLabel = useRef<string | null>(null);
	const originalDeckBoxPosition = useRef<{
		x: number;
		y: number;
	} | null>(null);
	const previousSegmentCount = useRef<number>(0);

	useEffect(() => {
		// Check if this is a new modal (not an update to existing modal)
		const blockIdChanged = currentBlockId.current !== block?.id;
		const edgeLabelChanged =
			currentEdgeLabel.current !== selectedEdge?.edgeLabel;

		if (animationInProgress.current && !blockIdChanged && !edgeLabelChanged) {
			return;
		}

		const currentSegmentCount = blockEdge?.segments?.length || 0;
		const segmentCountChanged =
			previousSegmentCount.current !== currentSegmentCount;

		if (
			!blockIdChanged &&
			!edgeLabelChanged &&
			originalDeckBoxPosition.current !== null &&
			!segmentCountChanged
		) {
			return;
		}

		currentBlockId.current = block?.id || null;
		currentEdgeLabel.current = selectedEdge?.edgeLabel || null;
		previousSegmentCount.current = currentSegmentCount;
		animationInProgress.current = true;

		// Auto-scroll canvas to ensure modal is visible
		if (block?.ref?.current && state.canvasBounds) {
			setTimeout(() => {
				const canvasContainer = document.getElementById('main-canvas');
				if (!canvasContainer) return;

				const blockElement = block.ref.current;
				const blockRect = blockElement.getBoundingClientRect();
				const canvasRect = canvasContainer.getBoundingClientRect();

				const MODAL_WIDTH = 260;
				const PADDING = 20;

				const popperElement = document.querySelector('[role="tooltip"]');
				const measuredHeight = popperElement?.getBoundingClientRect()?.height;
				const MODAL_HEIGHT =
					measuredHeight && measuredHeight > 0 ? measuredHeight : 600;

				const edgeLabel = selectedEdge?.edgeLabel;

				// Find parent deckbox for potential animation
				let parentDeckBoxId = null;
				if (block.type === BlockType.BUMPOUT) {
					const parentBox = state.blocks.find(
						(b) =>
							b.type === BlockType.DECKBOX &&
							b.bumpouts?.some((bump) => bump.id === block.id)
					);
					parentDeckBoxId = parentBox?.id;
				} else {
					const result = getParentBlock(
						state.blocks,
						block.parentBlock || { id: block.id, type: block.type }
					);
					parentDeckBoxId = result.parentDeckBoxId;
				}

				const deckBox = state.blocks.find(
					(b) => b.id === (parentDeckBoxId || block.id)
				);

				// Check if modal would render outside canvas and animate if needed
				if (edgeLabel === EdgeLabel.LEFT) {
					const modalLeftEdge = blockRect.left - MODAL_WIDTH - PADDING;

					if (modalLeftEdge < canvasRect.left && deckBox) {
						if (originalDeckBoxPosition.current === null) {
							originalDeckBoxPosition.current = {
								x: deckBox.position.x,
								y: deckBox.position.y,
							};
						}

						const moveDistance = Math.abs(modalLeftEdge - canvasRect.left) + 20;

						const startX = deckBox.position.x;
						const startY = deckBox.position.y;
						const targetX = startX + moveDistance;
						const targetY = startY;
						const duration = 500;
						const startTime = performance.now();

						const easeOutQuad = (t: number) => t * (2 - t);

						const animate = (currentTime: number) => {
							const elapsed = currentTime - startTime;
							const progress = Math.min(elapsed / duration, 1);
							const easedProgress = easeOutQuad(progress);

							const currentX = startX + (targetX - startX) * easedProgress;
							const currentY = startY + (targetY - startY) * easedProgress;

							const deltaX = currentX - startX;
							const deltaY = currentY - startY;

							const updatedDeckBox = {
								...deckBox,
								position: {
									x: currentX,
									y: currentY,
								},
								...(deckBox.landings?.length && {
									landings: deckBox.landings.map((landing) => ({
										...landing,
										position: {
											x:
												(landing.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(landing.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								...(deckBox.stairs?.length && {
									stairs: deckBox.stairs.map((stair) => ({
										...stair,
										position: {
											x:
												(stair.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(stair.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								...(deckBox.bumpouts?.length && {
									bumpouts: deckBox.bumpouts.map((bumpout) => ({
										...bumpout,
										position: {
											x:
												(bumpout.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(bumpout.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
							};

							dispatch({
								type: 'UPDATE_BLOCK',
								payload: updatedDeckBox,
							});

							if (progress < 1) {
								requestAnimationFrame(animate);
							} else {
								animationInProgress.current = false;
							}
						};

						requestAnimationFrame(animate);
					} else {
						animationInProgress.current = false;
					}
				} else if (edgeLabel === EdgeLabel.TOP) {
					const availableSpace = blockRect.top - canvasRect.top;

					if (availableSpace < MODAL_HEIGHT + PADDING && deckBox) {
						if (originalDeckBoxPosition.current === null) {
							originalDeckBoxPosition.current = {
								x: deckBox.position.x,
								y: deckBox.position.y,
							};
						}

						const moveDistance = MODAL_HEIGHT + PADDING - availableSpace + 20;

						const startX = deckBox.position.x;
						const startY = deckBox.position.y;
						const targetX = startX;
						const targetY = startY + moveDistance;
						const duration = 500;
						const startTime = performance.now();

						const easeOutQuad = (t: number) => t * (2 - t);

						const animate = (currentTime: number) => {
							const elapsed = currentTime - startTime;
							const progress = Math.min(elapsed / duration, 1);
							const easedProgress = easeOutQuad(progress);

							const currentX = startX + (targetX - startX) * easedProgress;
							const currentY = startY + (targetY - startY) * easedProgress;

							const deltaX = currentX - startX;
							const deltaY = currentY - startY;

							const updatedDeckBox = {
								...deckBox,
								position: {
									x: currentX,
									y: currentY,
								},
								...(deckBox.landings?.length && {
									landings: deckBox.landings.map((landing) => ({
										...landing,
										position: {
											x:
												(landing.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(landing.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								...(deckBox.stairs?.length && {
									stairs: deckBox.stairs.map((stair) => ({
										...stair,
										position: {
											x:
												(stair.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(stair.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								...(deckBox.bumpouts?.length && {
									bumpouts: deckBox.bumpouts.map((bumpout) => ({
										...bumpout,
										position: {
											x:
												(bumpout.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(bumpout.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
							};

							dispatch({
								type: 'UPDATE_BLOCK',
								payload: updatedDeckBox,
							});

							if (progress < 1) {
								requestAnimationFrame(animate);
							} else {
								animationInProgress.current = false;
							}
						};

						requestAnimationFrame(animate);
					} else {
						animationInProgress.current = false;
					}
				} else {
					// No animation needed for RIGHT and BOTTOM edges
					animationInProgress.current = false;
				}
			}, 150);
		}
	}, [
		block?.id,
		selectedEdge?.blockId,
		selectedEdge?.edgeLabel,
		state.canvasBounds,
	]);

	useEffect(() => {
		if (selectedEdge === null && originalDeckBoxPosition.current !== null) {
			const currentBlock = block;
			const currentBlocks = state.blocks;

			let parentDeckBoxId = null;
			if (currentBlock?.type === BlockType.BUMPOUT) {
				const parentBox = currentBlocks.find(
					(b) =>
						b.type === BlockType.DECKBOX &&
						b.bumpouts?.some((bump) => bump.id === currentBlock?.id)
				);
				parentDeckBoxId = parentBox?.id;
			} else {
				const result = getParentBlock(
					currentBlocks,
					currentBlock?.parentBlock || {
						id: currentBlock?.id || '',
						type: currentBlock?.type || BlockType.DECKBOX,
					}
				);
				parentDeckBoxId = result.parentDeckBoxId;
			}

			const deckBox = currentBlocks.find(
				(b) => b.id === (parentDeckBoxId || currentBlock?.id)
			);

			if (deckBox && originalDeckBoxPosition.current) {
				const deltaX = originalDeckBoxPosition.current.x - deckBox.position.x;
				const deltaY = originalDeckBoxPosition.current.y - deckBox.position.y;

				dispatch({
					type: 'UPDATE_BLOCK',
					payload: {
						...deckBox,
						position: {
							x: originalDeckBoxPosition.current.x,
							y: originalDeckBoxPosition.current.y,
						},
						...(deckBox.landings?.length && {
							landings: deckBox.landings.map((landing) => ({
								...landing,
								position: {
									x: (landing.position?.x || 0) + deltaX,
									y: (landing.position?.y || 0) + deltaY,
								},
							})),
						}),
						...(deckBox.stairs?.length && {
							stairs: deckBox.stairs.map((stair) => ({
								...stair,
								position: {
									x: (stair.position?.x || 0) + deltaX,
									y: (stair.position?.y || 0) + deltaY,
								},
							})),
						}),
						...(deckBox.bumpouts?.length && {
							bumpouts: deckBox.bumpouts.map((bumpout) => ({
								...bumpout,
								position: {
									x: (bumpout.position?.x || 0) + deltaX,
									y: (bumpout.position?.y || 0) + deltaY,
								},
							})),
						}),
					},
				});

				originalDeckBoxPosition.current = null;
			}
		}
	}, [selectedEdge, block, state.blocks, dispatch]);

	useEffect(() => {
		const segmentsLength =
			blockEdge?.segments.reduce((acc, seg) => {
				acc += seg.length;

				return acc;
			}, 0) || 0;

		if ((segmentsLength || 0) > (blockEdge?.length || 0)) {
			setError(
				`Total segments length (${segmentsLength / 12}ft) exceeds edge length (${(blockEdge?.length || 0) / 12}ft)`
			);
		} else if ((segmentsLength || 0) < (blockEdge?.length || 0)) {
			setError(
				`Total segments length (${segmentsLength / 12}ft) is less than edge length (${(blockEdge?.length || 0) / 12}ft)`
			);
		} else {
			setError(null);
		}
	}, [blockEdge]);

	const handleCloseDialog = () => {
		setSegmentLength(0);
		setSegmentType('');
		setStage(0);

		if (originalDeckBoxPosition.current) {
			// Use current block and state for restoration animation
			const currentBlock = block;
			const currentBlocks = state.blocks;

			let parentDeckBoxId = null;
			if (currentBlock?.type === BlockType.BUMPOUT) {
				const parentBox = currentBlocks.find(
					(b) =>
						b.type === BlockType.DECKBOX &&
						b.bumpouts?.some((bump) => bump.id === currentBlock.id)
				);
				parentDeckBoxId = parentBox?.id;
			} else {
				const result = getParentBlock(
					currentBlocks,
					currentBlock?.parentBlock || {
						id: currentBlock?.id || '',
						type: currentBlock?.type || BlockType.DECKBOX,
					}
				);
				parentDeckBoxId = result.parentDeckBoxId;
			}

			const deckBox = currentBlocks.find(
				(b) => b.id === (parentDeckBoxId || currentBlock?.id)
			);

			if (deckBox) {
				const startX = deckBox.position.x;
				const startY = deckBox.position.y;
				const targetX = originalDeckBoxPosition.current.x;
				const targetY = originalDeckBoxPosition.current.y;
				const duration = 500;
				const startTime = performance.now();

				const easeOutQuad = (t: number) => t * (2 - t);

				const animate = (currentTime: number) => {
					const elapsed = currentTime - startTime;
					const progress = Math.min(elapsed / duration, 1);
					const easedProgress = easeOutQuad(progress);

					const currentX = startX + (targetX - startX) * easedProgress;
					const currentY = startY + (targetY - startY) * easedProgress;

					const deltaX = currentX - deckBox.position.x;
					const deltaY = currentY - deckBox.position.y;

					const updatedDeckBox = {
						...deckBox,
						position: { x: currentX, y: currentY },
						...(deckBox.landings?.length && {
							landings: deckBox.landings.map((landing) => ({
								...landing,
								position: {
									x: (landing.position?.x || 0) + deltaX,
									y: (landing.position?.y || 0) + deltaY,
								},
							})),
						}),
						...(deckBox.stairs?.length && {
							stairs: deckBox.stairs.map((stair) => ({
								...stair,
								position: {
									x: (stair.position?.x || 0) + deltaX,
									y: (stair.position?.y || 0) + deltaY,
								},
							})),
						}),
						...(deckBox.bumpouts?.length && {
							bumpouts: deckBox.bumpouts.map((bumpout) => ({
								...bumpout,
								position: {
									x: (bumpout.position?.x || 0) + deltaX,
									y: (bumpout.position?.y || 0) + deltaY,
								},
							})),
						}),
					};

					dispatch({
						type: 'UPDATE_BLOCK',
						payload: updatedDeckBox,
					});

					if (progress < 1) {
						requestAnimationFrame(animate);
					} else {
						animationInProgress.current = false;
					}
				};

				requestAnimationFrame(animate);
			}

			originalDeckBoxPosition.current = null;
		}

		animationInProgress.current = false;
		currentBlockId.current = null;
		currentEdgeLabel.current = null;

		dispatch({
			type: 'SELECT_EDGE',
			payload: { blockId: null, edgeLabel: null },
		});
		dispatch({ type: 'CLEAR_SELECTION' });
	};

	const parentAttachedEdge = block?.edges.find((edge) => edge.attachedToParent);

	const sideEdgeLabels = block?.edges
		?.filter(
			(edge) =>
				edge.label != parentAttachedEdge?.label &&
				edge.label != getOppositeEdge(parentAttachedEdge?.label)
		)
		?.map((edge) => edge.label);

	let showActionButtons = true;

	if (block?.type == BlockType.STAIR) {
		showActionButtons = !sideEdgeLabels?.includes(blockEdge?.label);
	}

	const handleStartHeightChange = (inches) => {
		const startHeight = inches;
		const height = startHeight - (block?.dimensions.endHeight || 0);
		const numberOfSteps = Math.ceil(height / STEPS_COEFFICIENT);
		const depth = numberOfSteps * DEPTH_COEFFICIENT;

		const newDimensions = {
			width: block?.dimensions.width || 0,
			startHeight,
			height,
			numberOfSteps,
			depth,
			endHeight: block?.dimensions.endHeight || 0,
		};

		const newDisplayDimensions = {
			width:
				displayWidthDimension == 'width'
					? newDimensions.width
					: newDimensions.depth,
			height:
				displayWidthDimension == 'width'
					? newDimensions.depth
					: newDimensions.width,
		};

		const parentBlock = getBlock(state.blocks, block?.parentBlock?.id || '');

		if (newDimensions?.startHeight != parentBlock?.dimensions.height) {
			toastError({
				title: 'Block height not matching',
				description: `${block?.label} start height should be the same as ${parentBlock?.label} height`,
			});

			return;
		}

		if (newDimensions?.startHeight <= newDimensions?.endHeight) {
			toastError({
				title: 'Stair height is incorrect',
				description:
					'Stair start height should be greater than its Landing height',
			});

			return;
		}

		if (newDimensions?.height > MAX_STAIR_HEIGHT) {
			toastError({
				title: 'Stair height is incorrect',
				description: `Stair height cannot be higher than ${MAX_STAIR_HEIGHT / 12}ft`,
			});

			return;
		}

		const updatedBlock: Block = {
			...block,
			dimensions: newDimensions,
			displayDimensions: newDisplayDimensions,
		};

		const updateChildBlocksParams = {
			blocks: state.blocks,
			oldBlock: block,
			updatedBlock,
			dispatch,
		};

		dispatch({
			type: 'UPDATE_BLOCK',
			payload: updatedBlock,
		});

		updateChildBlocks(updateChildBlocksParams);
	};

	const handleEndHeightChange = (inches) => {
		const endHeight = inches;
		const height = (block?.dimensions.startHeight || 0) - endHeight;
		const numberOfSteps = Math.ceil(height / STEPS_COEFFICIENT);
		const depth = numberOfSteps * DEPTH_COEFFICIENT;

		const newDimensions = {
			width: block?.dimensions.width || 0,
			startHeight: block?.dimensions.startHeight || 0,
			height,
			numberOfSteps,
			depth,
			endHeight,
		};

		const newDisplayDimensions = {
			width:
				displayWidthDimension == 'width'
					? newDimensions.width
					: newDimensions.depth,
			height:
				displayWidthDimension == 'width'
					? newDimensions.depth
					: newDimensions.width,
		};

		const updatedBlock: Block = {
			...block,
			dimensions: newDimensions,
			displayDimensions: newDisplayDimensions,
		};

		const updateChildBlocksParams = {
			blocks: state.blocks,
			oldBlock: block,
			updatedBlock,
			dispatch,
		};

		dispatch({
			type: 'UPDATE_BLOCK',
			payload: updatedBlock,
		});

		updateChildBlocks(updateChildBlocksParams);
	};

	const handleAddSegment = () => {
		if (block && blockEdge) {
			block.edges = block?.edges.map((ed) => {
				if (ed.id == blockEdge.id) {
					const updatedEdge = { ...ed };

					const remainingLength =
						updatedEdge.length -
						updatedEdge.segments.reduce(
							(acc, segment) => acc + segment.length,
							0
						);

					updatedEdge.segments = [
						...updatedEdge.segments,
						{
							id: uuidv4(),
							edgeId: blockEdge?.id,
							blockId: block.id || '',
							length: Math.max(0, remainingLength),
							type: EdgeType.RAILING,
						},
					];

					return updatedEdge;
				} else {
					return ed;
				}
			});

			dispatch({ type: 'UPDATE_BLOCK', payload: block });
		}
	};

	return (
		<ArrowPopper
			open={true}
			anchorEl={anchorEl}
			placement={
				state.selectedEdge?.edgeLabel === EdgeLabel.TOP
					? 'top-start'
					: state.selectedEdge?.edgeLabel === EdgeLabel.BOTTOM
						? 'bottom-start'
						: state.selectedEdge?.edgeLabel === EdgeLabel.LEFT
							? 'left-start'
							: 'right-start'
			}
			onClickAway={handleCloseDialog}
			width="260px"
			zIndex={theme.zIndex.modal + 1}
			transformWrapperRef={transformWrapperRef}
			enableCanvasPan={true}
			disablePortal={true}
			clickAwayLockedRef={clickAwayLockedRef}
		>
			{stage == 0 && (
				<>
					<Stack>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{ p: theme.spacing(1, 1.25), pb: theme.spacing(0.5) }}
						>
							<Box>
								<DialogTitle
									component="div"
									sx={{ p: 0, fontSize: '1rem', fontWeight: 600 }}
								>
									Editing {selectedEdge?.edgeLabel} Edge
								</DialogTitle>
								<Typography
									variant="caption"
									sx={{ color: theme.palette.text.secondary }}
								>
									on {block?.label}
								</Typography>
							</Box>
							<IconButton onClick={handleCloseDialog} size="small">
								<CloseIcon />
							</IconButton>
						</Stack>

						{error && (
							<Typography
								sx={{
									color: 'orangered',
									fontSize: '12px',
									paddingX: '10px',
								}}
							>
								{error}
							</Typography>
						)}
					</Stack>

					<DialogContent
						sx={{
							p: theme.spacing(1.25, 1.25),
							maxHeight: '30vh',
							overflowY: 'auto',
							'&::-webkit-scrollbar': {
								width: '0.375rem',
							},
							'&::-webkit-scrollbar-track': {
								background: theme.palette.grey[100],
							},
							'&::-webkit-scrollbar-thumb': {
								background: theme.palette.grey[400],
								borderRadius: '0.1875rem',
							},
							'&::-webkit-scrollbar-thumb:hover': {
								background: theme.palette.grey[500],
							},
						}}
					>
						<Box>
							{block?.type == BlockType.STAIR &&
								blockEdge?.label ==
									getOppositeEdge(
										block.edges.find((edge) => edge.attachedToParent)?.label
									) && (
									<FeetInchesInput
										valueInches={block.dimensions?.endHeight || 0}
										labelInches="Landing Height"
										onChange={handleEndHeightChange}
										locked={true}
									/>
								)}
							{block?.type == BlockType.STAIR &&
								blockEdge?.attachedToParent && (
									<FeetInchesInput
										valueInches={block.dimensions?.startHeight || 0}
										labelInches="Start Height"
										onChange={handleStartHeightChange}
										locked={true}
									/>
								)}
						</Box>

						{blockEdge?.segments.map((segment, index) => (
							<Segment
								key={segment.id}
								segment={segment}
								segmentIndex={index}
								block={block}
								segmentsCount={blockEdge.segments.length}
								setError={setError}
								clickAwayLockedRef={clickAwayLockedRef}
							/>
						))}
					</DialogContent>

					{showActionButtons && (
						<DialogActions
							sx={{
								borderTop: '1px solid',
								borderColor: theme.palette.divider,
								p: theme.spacing(1, 1.25),
								gap: theme.spacing(0.75),
								justifyContent: 'center',
							}}
						>
							<Button
								onClick={handleAddSegment}
								variant="contained"
								sx={{
									textTransform: 'none',
									backgroundColor: '#E3FBE3',
									color: 'black',
									'&:hover': {
										color: theme.palette.common.white,
										backgroundColor: 'green',
									},
								}}
							>
								Add Segment
							</Button>
						</DialogActions>
					)}
				</>
			)}
			{stage == 1 && (
				<BlockOptions
					setStage={setStage}
					handleCloseDialog={handleCloseDialog}
					block={block}
					edge={blockEdge}
				/>
			)}
			{stage == 2 && (
				<SegmentOptions
					setStage={setStage}
					handleCloseDialog={handleCloseDialog}
					block={block}
					edge={blockEdge}
				/>
			)}
		</ArrowPopper>
	);
};

export default EdgeModalV3;
